import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { syncStatusBarNow } from "@/lib/statusBar";

/**
 * Keeps the native status bar (and web theme-color meta) in sync with the
 * background color painted at the top of the current page. Re-samples on
 * every route change plus a short delayed second pass to catch
 * skeleton→content swaps, lazy sections, and gradient headers.
 */
export function StatusBarSync() {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    let raf = 0;
    const t1 = window.setTimeout(() => syncStatusBarNow(), 250);
    const t2 = window.setTimeout(() => syncStatusBarNow(), 800);
    raf = window.requestAnimationFrame(() => syncStatusBarNow());

    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [pathname, search, hash]);

  // Observe body-level changes (modals/sheets/top-bar mode swaps) and resync,
  // throttled to avoid storms.
  useEffect(() => {
    if (typeof document === "undefined") return;
    let pending = false;
    const schedule = () => {
      if (pending) return;
      pending = true;
      window.setTimeout(() => {
        pending = false;
        syncStatusBarNow();
      }, 500);
    };
    const obs = new MutationObserver(schedule);
    obs.observe(document.body, {
      attributes: true,
      attributeFilter: ["class", "style"],
      subtree: false,
    });
    return () => obs.disconnect();
  }, []);

  return null;
}

export default StatusBarSync;
